import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FileChip,
  ChatInput,
  FileSelector,
} from '@/features/chat/shared/components';
import { FileType as GlobalFileType } from '../../../../types';
import { useOutputLanguage } from '../../../../hooks/useOutputLanguage';
import { SUPPORT_KEYWORDS } from '../constants';
import type { SavedConversation, Stage } from '../types';
import HistoryDropdown from './HistoryDropdown';
import SelectedFilesDropdown from './SelectedFilesDropdown';

interface InputAreaProps {
  directions: string;
  setDirections: (value: string) => void;
  onSubmit: (message?: string) => void;
  stage: Stage;
  isLoading: boolean;
  onStopLoading: () => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;

  // File state
  selectedFiles: string[];
  cachedFiles: GlobalFileType[];
  onFileSelect: (file: GlobalFileType) => void;

  // History
  showHistoryDropdown: boolean;
  setShowHistoryDropdown: (show: boolean) => void;
  externalShowHistoryDropdown?: boolean;
  savedConversations: SavedConversation[];
  isLoadingSessions: boolean;
  onLoadConversation: (conversationId: string) => void;
  onDeleteConversation: (conversationId: string, e?: React.MouseEvent) => void;
}

const InputArea: React.FC<InputAreaProps> = ({
  directions,
  setDirections,
  onSubmit,
  stage,
  isLoading,
  onStopLoading,
  inputRef,
  selectedFiles,
  cachedFiles,
  onFileSelect,
  showHistoryDropdown,
  setShowHistoryDropdown,
  externalShowHistoryDropdown,
  savedConversations,
  isLoadingSessions,
  onLoadConversation,
  onDeleteConversation,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguageName } = useOutputLanguage();
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [showSelectedFilesDropdown, setShowSelectedFilesDropdown] =
    useState(false);

  // Ref for external history dropdown click-outside detection
  const externalHistoryDropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside for external history dropdown
  useEffect(() => {
    if (!showHistoryDropdown || !externalShowHistoryDropdown) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        externalHistoryDropdownRef.current &&
        !externalHistoryDropdownRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('[data-history-trigger]')
      ) {
        setShowHistoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    showHistoryDropdown,
    externalShowHistoryDropdown,
    setShowHistoryDropdown,
  ]);

  // Detect support-related keywords
  const showSupportRedirect = useMemo(() => {
    if (!directions.trim()) return false;
    const lowerInput = directions.toLowerCase();
    return SUPPORT_KEYWORDS.some((keyword) => lowerInput.includes(keyword));
  }, [directions]);

  const handleGoToSettings = () => {
    navigate('/settings/account');
    setDirections('');
  };

  const handleLoadConversation = (conversationId: string) => {
    onLoadConversation(conversationId);
    setShowHistoryDropdown(false);
  };

  const placeholder =
    selectedFiles.length > 0
      ? t(
          'drawer.multiAskAI.searchPlaceholder.withFiles',
          `Ask anything about these recordings... (Output: ${currentLanguageName})`,
        )
      : t(
          'drawer.multiAskAI.searchPlaceholder.noFiles',
          `Ask anything... (Output: ${currentLanguageName})`,
        );

  let chips: Array<{ element: React.ReactElement }> = [];
  if (selectedFiles.length > 0) {
    chips = [
      {
        element: (
          <FileChip
            fileCount={selectedFiles.length}
            fileName={cachedFiles[0]?.name}
            isLoading={selectedFiles.length > 0 && cachedFiles.length === 0}
            onClick={() =>
              setShowSelectedFilesDropdown(!showSelectedFilesDropdown)
            }
            onClear={() => {
              onFileSelect({} as GlobalFileType); // Signal to clear all - handled in parent
            }}
          />
        ),
      },
    ];
  }

  return (
    <div className="flex flex-col gap-2">
      {showSupportRedirect && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleGoToSettings}
            className="px-3 py-1.5 text-xs text-muted-foreground bg-muted/50 hover:bg-muted rounded-full transition-colors"
          >
            Looking for account settings?
          </button>
        </div>
      )}
      <ChatInput
        value={directions}
        onChange={setDirections}
        onSubmit={onSubmit}
        placeholder={placeholder}
        disabled={stage === 'processing'}
        disableSend={false}
        isLoading={isLoading}
        loadingIcon="square"
        onStopLoading={onStopLoading}
        inputRef={inputRef}
        chips={chips}
        leftButton={{
          icon: <Plus className="h-4 w-4 text-foreground" />,
          onClick: () => {
            if (showHistoryDropdown) {
              setShowHistoryDropdown(false);
            }
            setShowFileDropdown(!showFileDropdown);
          },
          ariaLabel: 'Add context',
        }}
        inactiveOpacity={false}
        enableDictation
        autoSendOnDictation
        enableMention
        selectedFiles={selectedFiles}
        onMentionSelect={onFileSelect}
      >
        {/* File Dropdown */}
        <Popover open={showFileDropdown} onOpenChange={setShowFileDropdown}>
          <PopoverTrigger asChild>
            <span />
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="start"
            sideOffset={8}
            alignOffset={0}
            collisionPadding={16}
            className="p-0 w-auto border-0 shadow-none"
          >
            <FileSelector
              selectedFiles={selectedFiles}
              onFileSelect={onFileSelect}
            />
          </PopoverContent>
        </Popover>

        {/* External History Dropdown */}
        {showHistoryDropdown && externalShowHistoryDropdown && (
          <div
            ref={externalHistoryDropdownRef}
            className="fixed top-14 right-6 z-[4100]"
          >
            <div className="w-96 max-h-96 overflow-hidden bg-popover border border-border rounded-md shadow-md animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200">
              <HistoryDropdown
                savedConversations={savedConversations}
                isLoadingSessions={isLoadingSessions}
                onLoadConversation={handleLoadConversation}
                onDeleteConversation={onDeleteConversation}
              />
            </div>
          </div>
        )}

        {/* Internal History Dropdown */}
        {showHistoryDropdown && !externalShowHistoryDropdown && (
          <Popover
            open={showHistoryDropdown}
            onOpenChange={setShowHistoryDropdown}
          >
            <PopoverTrigger asChild>
              <span />
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              sideOffset={8}
              alignOffset={10}
              collisionPadding={16}
              className="p-0 w-96 border-0 shadow-none"
            >
              <div className="w-96 max-h-96 overflow-hidden bg-popover border border-border rounded-md shadow-md">
                <HistoryDropdown
                  savedConversations={savedConversations}
                  isLoadingSessions={isLoadingSessions}
                  onLoadConversation={handleLoadConversation}
                  onDeleteConversation={onDeleteConversation}
                />
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Selected Files Dropdown */}
        <SelectedFilesDropdown
          open={showSelectedFilesDropdown}
          onOpenChange={setShowSelectedFilesDropdown}
          cachedFiles={cachedFiles}
          onFileSelect={onFileSelect}
        />
      </ChatInput>
    </div>
  );
};

export default InputArea;
